import Hero from './components/Hero';
import NativeVideoPlayer from './components/NativeVideoPlayer';
import Brand from './components/Brand';
import ValuePillars from './components/ValuePillars';
import PillarDeepDive from './components/PillarDeepDive';
import Testimonials from './components/Testimonials';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'NIMO AI 智能眼镜',
      url: `${SITE_URL}/`,
      description: 'NIMO AI 智能眼镜，过滤数字干扰，让你专注当下。',
      inLanguage: 'zh-CN',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/?s={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'NIMO AI',
      url: `${SITE_URL}/`,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/dd0da8653222058c369d35079551e967.png',
      },
      description: 'NIMO AI 是专注于光波导智能眼镜与 AI 专注力技术的科技品牌。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'store@nimofantasy.ai',
        contactType: 'customer support',
        availableLanguage: 'Chinese',
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      url: `${SITE_URL}/`,
      name: 'NIMO AI 智能眼镜',
      description: 'NIMO AI 智能眼镜，过滤数字干扰，让你专注当下。',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'zh-CN',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  url: `${SITE_URL}/#faq`,
  mainEntity: [
    {
      '@type': 'Question',
      name: '为什么 NIMO 没有摄像头？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '这是我们的战略选择，不是技术限制。无摄像头设计让 NIMO 可以进入医院 ICU、会议室、健身房等任何场景，无需担心隐私风险或他人的怀疑目光。它也符合 HIPAA/GDPR 等严格的隐私法规，是进入医疗等 B2B 场景的"准入通行证"。',
      },
    },
    {
      '@type': 'Question',
      name: '单色显示会不会影响使用体验？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '恰恰相反，单色显示有三大优势：续航更持久，全天使用无需充电；视觉疲劳降低，长时间阅读不累眼；信息更纯粹，不会被色彩分散注意力。就像 Kindle 的电子墨水屏——长时间阅读不累眼，因为它只做一件事并做到出色。',
      },
    },
    {
      '@type': 'Question',
      name: 'NIMO 和普通智能眼镜有什么区别？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '普通智能眼镜追求"更多功能"，NIMO 追求"更少干扰"。我们的 AI 引擎会过滤数字噪音，只保留真正需要你注意的内容。35 克的超轻设计让你可以全天佩戴而不感到负担。我们不是做加法，而是做减法。',
      },
    },
    {
      '@type': 'Question',
      name: '能在阳光下看清屏幕吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NIMO 采用体全息光波导技术，亮度高达 3500 nit，即使在阳光直射下也能清晰阅读。相比之下，大多数智能眼镜在室外需要寻找阴影区域才能看清屏幕。',
      },
    },
    {
      '@type': 'Question',
      name: '需要配近视镜片吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NIMO 支持定制近视镜片（100-800 度）。如果你日常佩戴眼镜，可以选择配镜版本；如果视力正常或佩戴隐形眼镜，可以选择标准版。',
      },
    },
    {
      '@type': 'Question',
      name: '续航时间有多长？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '正常使用（每天查看信息 50-80 次）可使用 8-10 小时。重度用户续航约 6 小时。充电时间 90 分钟，支持快充。整机待机时间可达 72 小时。',
      },
    },
    {
      '@type': 'Question',
      name: '防水吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NIMO 的防护等级是 IP54，可以防护日常汗水、小雨和水花飞溅。不支持游泳或淋浴时佩戴，不建议完全浸入水中。',
      },
    },
    {
      '@type': 'Question',
      name: '支持哪些手机系统？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '支持 iOS 13 及以上、Android 8.0 及以上。通过蓝牙 5.2 连接手机，配对过程不到 1 分钟。',
      },
    },
    {
      '@type': 'Question',
      name: '数据安全吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NIMO 的所有数据处理都在本地完成，不上传云端。我们通过了 ISO 27001 信息安全认证和独立第三方隐私审计。你的信息完全由你掌控，我们无法访问。',
      },
    },
    {
      '@type': 'Question',
      name: '可以自定义信息过滤规则吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '完全可以。你可以在 APP 中设置哪些 APP 的通知会立即显示、哪些联系人的消息优先级最高，以及工作时段和休息时段的不同过滤策略。AI 引擎会学习你的习惯，逐渐优化过滤规则。',
      },
    },
    {
      '@type': 'Question',
      name: '戴久了会不会头晕？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NIMO 的显示位置经过精心设计，位于视野下方，不会遮挡正常视线。单色显示降低了视觉刺激强度，长时间使用不易疲劳。如果你初次使用，建议从每天 2-3 小时开始逐步适应。',
      },
    },
    {
      '@type': 'Question',
      name: '可以试用吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '个人用户可申请 7 天无理由退换（需保持产品完好）。企业和医疗机构可申请 30-60 天试点项目，免费提供设备和技术支持。',
      },
    },
    {
      '@type': 'Question',
      name: '质保政策是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'NIMO 提供 1 年质保，覆盖非人为损坏的硬件故障。质保期内免费维修或更换。超过质保期后，可享受优惠维修服务。',
      },
    },
    {
      '@type': 'Question',
      name: 'NIMO 适合我吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '如果你经常被手机通知打断工作、需要在开会/查房/运动时快速查看信息、重视隐私安全，或者希望减少数字干扰重获专注力，NIMO 很可能适合你。',
      },
    },
    {
      '@type': 'Question',
      name: '可以分期付款吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '支持花呗、信用卡分期（3/6/12 期），具体费率以支付平台为准。',
      },
    },
  ],
};

export default function Home() {
  return (
    <div className="w-full overflow-x-hidden">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main>
        <Hero />
        <NativeVideoPlayer />
        <Brand />
        <ValuePillars />
        <PillarDeepDive />
        <Testimonials />
      </main>
    </div>
  );
}